import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Receipt, Plus, ChevronLeft, ChevronRight, Search, 
  Fuel, Car, ParkingCircle, Wrench, Shield, MoreHorizontal,
  Loader2, Trash2, Camera, Calendar, Euro, Filter
} from 'lucide-react';

const CATEGORY_CONFIG = {
  fuel: { name: 'Carburant', icon: Fuel, color: '#F59E0B', bg: '#FFFBEB' },
  toll: { name: 'Péages', icon: Car, color: '#3B82F6', bg: '#EFF6FF' },
  parking: { name: 'Parking', icon: ParkingCircle, color: '#8B5CF6', bg: '#F5F3FF' },
  maintenance: { name: 'Entretien', icon: Wrench, color: '#EF4444', bg: '#FEF2F2' },
  insurance: { name: 'Assurance', icon: Shield, color: '#10B981', bg: '#ECFDF5' },
  other: { name: 'Autre', icon: MoreHorizontal, color: '#6B7280', bg: '#F9FAFB' }
};

const OptiTrajetExpenses = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [mounted, setMounted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchExpenses = useCallback(async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    try {
      const [expensesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/optitrajet/expenses?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/optitrajet/expenses/stats?period=month`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (expensesRes.ok) {
        const data = await expensesRes.json();
        setExpenses(data.expenses || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Fetch expenses error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    setMounted(true);
    fetchExpenses();
  }, [fetchExpenses]);

  const deleteExpense = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    
    const token = localStorage.getItem('optitrajet_token');
    try {
      await fetch(`${API_URL}/api/optitrajet/expenses/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExpenses(expenses.filter(e => e.id !== id));
      fetchExpenses(); // Refresh stats
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         expense.category_info?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || expense.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/optitrajet/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800">
              Dépenses
            </h1>
          </div>
          <div className="flex gap-2">
            <Link to="/optitrajet/expenses/scan">
              <Button variant="outline" className="rounded-xl h-10 border-slate-200">
                <Camera className="h-4 w-4 mr-1" />
                Scanner
              </Button>
            </Link>
            <Link to="/optitrajet/expenses/new">
              <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl h-10">
                <Plus className="h-4 w-4 mr-1" />
                Ajouter
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats Summary */}
        {stats && (
          <Card className={`mb-6 border-0 shadow-lg overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <CardContent className="p-0">
              <div className="p-4 bg-gradient-to-r from-amber-500 to-orange-500">
                <div className="text-white">
                  <p className="text-sm opacity-90">Total ce mois</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {stats.total_amount.toLocaleString()} €
                  </p>
                  <p className="text-sm opacity-90">{stats.total_count} dépenses</p>
                </div>
              </div>
              
              {/* Category breakdown */}
              {stats.by_category?.length > 0 && (
                <div className="p-4 bg-white">
                  <div className="flex flex-wrap gap-2">
                    {stats.by_category.map((cat) => {
                      const config = CATEGORY_CONFIG[cat.category] || CATEGORY_CONFIG.other;
                      return (
                        <button
                          key={cat.category}
                          onClick={() => setFilterCategory(filterCategory === cat.category ? '' : cat.category)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                            filterCategory === cat.category 
                              ? 'ring-2 ring-offset-1' 
                              : 'hover:opacity-80'
                          }`}
                          style={{ 
                            backgroundColor: config.bg, 
                            color: config.color,
                            ringColor: config.color
                          }}
                        >
                          <span className="font-medium">{cat.amount}€</span>
                          <span className="opacity-75">{config.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className={`mb-6 transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Rechercher une dépense..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl border-slate-200"
            />
            {filterCategory && (
              <button
                onClick={() => setFilterCategory('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-xs bg-slate-100 px-2 py-1 rounded-full hover:bg-slate-200"
              >
                ✕ {CATEGORY_CONFIG[filterCategory]?.name}
              </button>
            )}
          </div>
        </div>

        {/* Expenses List */}
        <div className={`space-y-3 transition-all duration-500 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {filteredExpenses.length > 0 ? (
            filteredExpenses.map((expense, idx) => {
              const config = CATEGORY_CONFIG[expense.category] || CATEGORY_CONFIG.other;
              const Icon = config.icon;
              return (
                <Card 
                  key={expense.id}
                  className="border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: config.bg }}
                      >
                        <Icon className="h-6 w-6" style={{ color: config.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span 
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ backgroundColor: config.bg, color: config.color }}
                          >
                            {config.name}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDate(expense.date)}
                          </span>
                        </div>
                        <p className="font-medium text-slate-800 truncate">
                          {expense.description || 'Dépense'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p style={{ fontFamily: "'Outfit', sans-serif" }} className="font-bold text-lg text-slate-800">
                          {expense.amount} €
                        </p>
                      </div>
                      <button
                        onClick={() => deleteExpense(expense.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="border border-dashed border-slate-200 bg-slate-50/50">
              <CardContent className="p-8 text-center">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">
                  {searchQuery || filterCategory ? 'Aucune dépense trouvée' : 'Aucune dépense enregistrée'}
                </p>
                <div className="flex justify-center gap-3">
                  <Link to="/optitrajet/expenses/scan">
                    <Button variant="outline">
                      <Camera className="h-4 w-4 mr-2" />
                      Scanner un reçu
                    </Button>
                  </Link>
                  <Link to="/optitrajet/expenses/new">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Floating Action Button */}
      <Link to="/optitrajet/expenses/new" className="lg:hidden">
        <button className="fixed bottom-20 right-4 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-300 text-white z-50 transition-transform hover:scale-105 active:scale-95">
          <Plus className="h-7 w-7" />
        </button>
      </Link>
    </div>
  );
};

export default OptiTrajetExpenses;
